// // components/IncomingCallScreen.js
// import React, { useEffect } from "react";
// import { Button, Avatar, Typography, CircularProgress, Box } from "@mui/material";

// /**
//  * Defensive incoming call UI.
//  * - caller may be null or have different field names.
//  * - auto-declines after 30s (cleans up timer).
//  */
// const IncomingCallScreen = ({ caller = { name: "Unknown", img: "" }, onAccept, onDecline, meetingName }) => {
//   // Keep timer cleanup robust, include both handlers in dep array
//   useEffect(() => {
//     const timer = setTimeout(() => {
//       try {
//         onDecline?.();
//       } catch (err) {
//         console.error("Error calling onDecline:", err);
//       }
//     }, 30000);

//     return () => clearTimeout(timer);
//   }, [onDecline, onAccept]);

//   const displayName = caller?.name ?? `${caller?.firstName ?? ""} ${caller?.lastName ?? ""}`.trim() 

//   return (
//     <Box
//       sx={{
//         position: "fixed",
//         top: 0,
//         left: 0,
//         right: 0,
//         bottom: 0,
//         backgroundColor: "rgba(0,0,0,0.75)",
//         display: "flex",
//         flexDirection: "column",
//         justifyContent: "center",
//         alignItems: "center",
//         zIndex: 14000, // high z-index to overlay app
//         color: "white",
//         p: 2,
//       }}
//     >
//       <CircularProgress
//         size={80}
//         thickness={2}
//         sx={{
//           position: "absolute",
//           color: "rgba(255,255,255,0.08)",
//         }}
//       />

//       <Avatar
//         src={caller?.img ?? caller?.proFile ?? ""}
//         sx={{
//           width: 120,
//           height: 120,
//           mb: 2,
//           border: "3px solid rgba(76,175,80,0.9)",
//         }}
//       />

//       <Typography variant="h4" sx={{ mb: 1, textAlign: "center" }}>
//         {displayName || "Unknown"}
//       </Typography>

//       <Typography variant="h6" sx={{ mb: 3 }}>
//         is calling...
//       </Typography>

//       {meetingName && (
//         <Typography variant="body1" sx={{ mb: 3 }}>
//           Meeting: {meetingName}
//         </Typography>
//       )}

//       <Box sx={{ display: "flex", gap: 2 }}>
//         <Button
//           variant="contained"
//           color="error"
//           sx={{ width: 150, height: 50 }}
//           onClick={() => onDecline && onDecline()}
//         >
//           DECLINE
//         </Button>

//         <Button
//           variant="contained"
//           color="success"
//           sx={{ width: 150, height: 50 }}
//           onClick={() => onAccept && onAccept()}
//         >
//           ACCEPT
//         </Button>
//       </Box>
//     </Box>
//   );
// };

// export default IncomingCallScreen;

// Enhanced IncomingCallScreen with better error handling and user experience

import React, { useEffect, useState } from "react";
import { 
  Button, 
  Avatar, 
  Typography, 
  CircularProgress, 
  Box,
  Paper,
  IconButton,
  LinearProgress
} from "@mui/material";
import CallEndIcon from "@mui/icons-material/CallEnd";
import CallIcon from "@mui/icons-material/Call";
import PersonIcon from "@mui/icons-material/Person";
import { useSelector } from "react-redux";

/**
 * Enhanced incoming call UI with:
 * - Better caller information display
 * - Auto-decline timer with visual feedback
 * - Robust error handling
 * - Improved accessibility
 */
const IncomingCallScreen = ({ 
  caller = { name: "Unknown", img: "" }, 
  onAccept, 
  onDecline, 
  meetingName,
  autoDeclineTimeout = 30000 // 30 seconds default
}) => {

  console.log("IncomingCallScreen rendered with caller:", caller);
  const [timeLeft, setTimeLeft] = useState(autoDeclineTimeout / 1000);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasResponded, setHasResponded] = useState(false);

  // Auto-decline timer with visual feedback
  useEffect(() => {
    if (hasResponded) return; // Don't start timer if already responded

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleDecline();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const autoDeclineTimer = setTimeout(() => {
      handleDecline();
    }, autoDeclineTimeout);

    return () => {
      clearInterval(timer);
      clearTimeout(autoDeclineTimer);
    };
  }, [hasResponded, autoDeclineTimeout]);

  // Enhanced caller name display
const displayName =caller?.name?.trim() || `${caller?.firstName ?? ""} ${caller?.lastName ?? ""}`.trim() || (caller?.contact_ref_id ? `User ${caller.contact_ref_id}` : caller?.id ? `User ${caller.id}` : "") || "Unknown Caller";


 const handleAccept = async () => {
    if (isProcessing || hasResponded) return;
    
    setIsProcessing(true);
    setHasResponded(true);

    try {
      await onAccept?.();
    } catch (err) {
      console.error("Error accepting call:", err);
      setIsProcessing(false);
      setHasResponded(false);
    }
  };

  const handleDecline = () => {
    if (isProcessing || hasResponded ) return;
    
    setHasResponded(true);
    try {
      onDecline?.();
    } catch (err) {
      console.error("Error declining call:", err);
    }
  };

  // Calculate progress percentage for the timer
  const progressPercentage = ((autoDeclineTimeout / 1000 - timeLeft) / (autoDeclineTimeout / 1000)) * 100;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        backdropFilter: "blur(5px)",
      }}
    >
      <Paper
        elevation={24}
        sx={{
          p: 4,
          textAlign: "center",
          borderRadius: 3,
          maxWidth: 400,
          width: "90%",
          bgcolor: "white",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Auto-decline progress indicator */}
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            bgcolor: "rgba(255, 0, 0, 0.1)",
            "& .MuiLinearProgress-bar": {
              bgcolor: "error.main"
            }
          }}
        />

        {/* Incoming call indicator */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="primary" fontWeight="bold">
            Incoming Call
          </Typography>
        </Box>

        {/* Caller Avatar */}
        <Box sx={{ mb: 2 }}>
          <Avatar
            src={caller?.img}
            alt={displayName}
            sx={{ 
              width: 80, 
              height: 80, 
              mx: "auto",
              border: "3px solid",
              borderColor: "primary.main"
            }}
          >
            {!caller?.img && <PersonIcon sx={{ fontSize: 40 }} />}
          </Avatar>
        </Box>

        {/* Caller Information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" gutterBottom fontWeight="medium">
            {displayName}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" gutterBottom>
            is calling...
          </Typography>

          {meetingName && (
            <Box sx={{ mt: 1, p: 1, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Meeting:</strong> {meetingName}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Timer display */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="error.main">
            Auto-decline in {timeLeft}s
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 3, justifyContent: "center" }}>
          <IconButton
            onClick={handleDecline}
            disabled={isProcessing}
            size="large"
            sx={{
              bgcolor: "error.main",
              color: "white",
              width: 60,
              height: 60,
              "&:hover": {
                bgcolor: "error.dark"
              },
              "&:disabled": {
                bgcolor: "grey.400"
              }
            }}
          >
            <CallEndIcon sx={{ fontSize: 24 }} />
          </IconButton>

          <IconButton
            onClick={handleAccept}
            disabled={isProcessing}
            size="large"
            sx={{
              bgcolor: "success.main",
              color: "white",
              width: 60,
              height: 60,
              "&:hover": {
                bgcolor: "success.dark"
              },
              "&:disabled": {
                bgcolor: "grey.400"
              }
            }}
          >
            {isProcessing ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              <CallIcon sx={{ fontSize: 24 }} />
            )}
          </IconButton>
        </Box>

        {/* Button Labels */}
        <Box sx={{ display: "flex", gap: 3, justifyContent: "center", mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ width: 60, textAlign: "center" }}>
            DECLINE
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ width: 60, textAlign: "center" }}>
            {isProcessing ? "JOINING..." : "ACCEPT"}
          </Typography>
        </Box>

        {/* Caller details if available */}
        {(caller?.contact_ref_id || caller?.id) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "grey.200" }}>
            <Typography variant="caption" color="text.secondary">
              ID: {caller?.contact_ref_id ?? caller?.id}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default IncomingCallScreen;